import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from 'react';
import axios from 'axios';
import authService from '../services/auth.service';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef(null);

  useEffect(() => {
    const current = authService.getCurrentUser();
    if (current) setUser(current);
    setLoading(false);
  }, []);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        if ((status === 401 || status === 403) && userRef.current) {
          authService.logout();
          setUser(null);
          if (typeof window !== 'undefined') {
            window.alert('You have been logged out. Please sign in again.');
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [setUser]);

  const login = async (credentials) => {
    const res = await authService.login(credentials);
    if (res?.access) setUser(res);
    return res;
  };

  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
  }, []);

  const signup = async (data) => {
    return authService.signup(data);
  };

  const verifyEmail = async (email, code) => {
    return authService.verifyEmail(email, code);
  };

  const resendVerification = async (email) => {
    return authService.resendVerification(email);
  };

  const requestPasswordReset = async (email) => {
    return authService.requestPasswordReset(email);
  };

  const verifyPasswordResetCode = async (email, code) => {
    return authService.verifyPasswordResetCode(email, code);
  };

  const resetPasswordWithCode = async (data) => {
    return authService.resetPasswordWithCode(data);
  };

  const requestOtp = async (data) => {
    return authService.requestOtp(data);
  };

  const verifyOtp = async (data) => {
    const res = await authService.verifyOtp(data);
    if (res?.access) {
      setUser(res);
    }
    return res;
  };

  const updateUserData = useCallback((patch) => {
    setUser((prev) => {
      if (!prev) {
        return prev;
      }

      let nextValue;

      if (typeof patch === 'function') {
        nextValue = patch(prev);
      } else if (prev.user && typeof prev.user === 'object') {
        nextValue = { ...prev, user: { ...prev.user, ...(patch || {}) } };
      } else {
        nextValue = { ...prev, ...(patch || {}) };
      }

      if (!nextValue) {
        localStorage.removeItem('user');
        return nextValue;
      }

      try {
        localStorage.setItem('user', JSON.stringify(nextValue));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to persist user updates', error);
      }

      return nextValue;
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        requestOtp,
        verifyOtp,
        signup,
        verifyEmail,
        resendVerification,
        requestPasswordReset,
        verifyPasswordResetCode,
        resetPasswordWithCode,
        updateUserData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
